import React from 'react';
import {
  tableHeaderRowClass, tableHeaderCellClass, tableRowClass, tableDataCellClass,
} from 'features/activity/styles/dataTableStyles';
import Icon from 'shared/components/Icon';
import { TARGETS } from 'features/evals3/templates';

/**
 * The evals list as the Evals page shows it: status dot, what it scores (the
 * Traces icon for a run, conversation or span), name, description, the last 30
 * days as passed | failed over a thin bar, last updated, last scored.
 * The page and the Ask chat render the same rows, so an eval reads the same
 * wherever it is listed. A row opens the eval; enabling and disabling
 * live on the eval's own page.
 */

export function relative(iso) {
  if (!iso) return '—';
  const sec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60); if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60); if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24); if (day < 30) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

/** Enabled: a green dot; disabled: a hollow one. The same mark the runs and issues tables use. */
export function StatusDot({ enabled }) {
  return (
    <span aria-hidden className={`inline-block h-2 w-2 shrink-0 rounded-full ${enabled ? 'bg-[color:var(--feedback-success)]' : 'border border-[color:var(--text-disabled)]'}`} />
  );
}

const targetLabel = (t) => TARGETS.find((x) => x.value === t)?.label || t;

/** The icons the Traces tabs use for each level. */
export const LEVEL_ICON = { run: 'timeline', conversation: 'conversation', span: 'span' };

/** What an eval scores, as the Traces icon for it, named on hover. */
export function TargetPill({ target }) {
  const label = targetLabel(target);
  return (
    <span className="inline-flex shrink-0 text-[color:var(--text-muted)]" title={`Scores each ${String(label).toLowerCase()}`} role="img" aria-label={label}>
      <Icon name={LEVEL_ICON[target] || 'timeline'} size={14} color="currentColor" />
    </span>
  );
}

/**
 * The last 30 days as passed | failed counts — the same cell as the current
 * evals' table: counts, not a rate, because a rate hides its sample size
 * (90% of ten reads the same as 90% of a thousand).
 */
export function ResultCell({ stats }) {
  if (!stats || !stats.total) return <span className="text-[12px] text-[color:var(--text-disabled)]">—</span>;
  const passed = Number(stats.passed || 0);
  const failed = Number(stats.failed || 0);
  if (passed + failed === 0) {
    return <span className="text-[12px] tabular-nums text-[color:var(--text-muted)]" title="Scored, but nothing resolved to pass or fail yet">{stats.total.toLocaleString()} scored</span>;
  }
  const rate = Math.round((passed / (passed + failed)) * 100);
  return (
    // The colours carry the meaning; a screen reader gets it in words.
    <span className="flex w-[120px] flex-col gap-1" aria-label={`${passed} passed, ${failed} failed in the last 30 days`} title={`${rate}% passed`}>
      <span className="inline-flex items-baseline gap-1.5 whitespace-nowrap text-[12px] tabular-nums" aria-hidden>
        <span className="font-semibold" style={{ color: 'var(--feedback-success)' }}>{passed.toLocaleString()}</span>
        <span className="text-[color:var(--border-strong)]">|</span>
        <span className="font-semibold" style={{ color: failed > 0 ? 'var(--feedback-error)' : 'var(--text-disabled)' }}>{failed.toLocaleString()}</span>
      </span>
      <span className="flex h-[3px] w-full overflow-hidden rounded-full" style={{ background: failed > 0 ? 'var(--feedback-error)' : 'var(--border-default)' }} aria-hidden>
        <span style={{ width: `${rate}%`, background: 'var(--feedback-success)' }} />
      </span>
    </span>
  );
}

/** Pass `picked` (a Set of ids) with `onToggle` and `onToggleAll` to add the tick column; without them the table is unchanged. */
export default function EvalsTable({ rows, onOpen, query = '', picked, onToggle, onToggleAll }) {
  const selectable = Boolean(picked && onToggle);
  const allPicked = selectable && rows.length > 0 && rows.every((r) => picked.has(r.id));
  return (
    <div className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface shadow-token-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-left" style={{ fontFamily: 'Inter, sans-serif' }}>
          <thead>
            <tr className={tableHeaderRowClass}>
              {selectable && (
                <th scope="col" className={`${tableHeaderCellClass} w-8`}>
                  <input type="checkbox" checked={allPicked} aria-label={allPicked ? 'Clear the selection' : 'Select all'} title={allPicked ? 'Clear the selection' : 'Select all'}
                    onChange={() => onToggleAll && onToggleAll(!allPicked)} />
                </th>
              )}
              <th scope="col" className={tableHeaderCellClass}>Name</th>
              <th scope="col" className={tableHeaderCellClass}>Description</th>
              <th scope="col" className={tableHeaderCellClass}>30d result</th>
              <th scope="col" className={tableHeaderCellClass}>Last updated</th>
              <th scope="col" className={tableHeaderCellClass}>Last scored</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className={`${tableRowClass} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--state-focus-ring)]`}
                tabIndex={0} aria-label={`Open ${r.name}`}
                onClick={() => onOpen(r)}
                onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onOpen(r); } }}>
                {selectable && (
                  <td className={`${tableDataCellClass} w-8`} onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={picked.has(r.id)} aria-label={`Select ${r.name}`} onChange={() => onToggle(r.id)} />
                  </td>
                )}
                <td className={tableDataCellClass}>
                  <span className="flex min-w-0 items-center gap-2">
                    <StatusDot enabled={Boolean(r.enabled)} />
                    <TargetPill target={r.target} />
                    <span className="truncate font-medium">{r.name}</span>
                  </span>
                </td>
                <td className={`${tableDataCellClass} max-w-[280px]`}>
                  <span className="line-clamp-1 text-[color:var(--text-muted)]">{r.description || <span className="text-[color:var(--text-disabled)]">—</span>}</span>
                </td>
                <td className={tableDataCellClass}><ResultCell stats={r.stats_30d} /></td>
                <td className={`${tableDataCellClass} whitespace-nowrap`} title={r.updated_at ? new Date(r.updated_at).toLocaleString() : undefined}><span className="text-[color:var(--text-muted)]">{relative(r.updated_at)}</span></td>
                <td className={`${tableDataCellClass} whitespace-nowrap`} title={r.last_scored_at ? new Date(r.last_scored_at).toLocaleString() : 'Nothing scored yet'}>
                  {r.last_scored_at ? <span className="text-[color:var(--text-muted)]">{relative(r.last_scored_at)}</span> : <span className="text-[color:var(--text-disabled)]">—</span>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={selectable ? 6 : 5} className="px-3 py-10 text-center text-[12px] text-[color:var(--text-muted)]">{query ? `Nothing matches “${query}”.` : 'Nothing matches.'}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
